import { Controller } from "@hotwired/stimulus"
import rrwebPlayer from "rrweb-player"

// Connects to data-controller="autobot-recording-modal"
export default class extends Controller {
  static targets = ["modal", "player", "title", "expires", "openLink", "downloadLink", "error"]

  connect() {
    this.boundKeydown = this._handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown, true)
    this.playerInstance = null
    this.downloadUrl = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown, true)
    this._resetPlayer()
  }

  async open(event) {
    event.preventDefault()

    const trigger = event.currentTarget
    const recordingUrl = trigger.dataset.recordingUrl

    if (!recordingUrl || !this.hasModalTarget) return

    const title = (trigger.dataset.recordingTitle || "").trim()
    this.titleTarget.textContent = title.length > 0 ? title : "Session replay"

    const expiresText = (trigger.dataset.recordingExpiresText || "").trim()
    if (expiresText.length > 0) {
      this.expiresTarget.textContent = expiresText
      this.expiresTarget.classList.remove("hidden")
    } else {
      this.expiresTarget.textContent = ""
      this.expiresTarget.classList.add("hidden")
    }

    this._resetPlayer()
    this._openModal()

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }

    try {
      const response = await fetch(recordingUrl, { headers: { Accept: "application/json" } })
      if (!response.ok) {
        throw new Error(`Recording fetch failed (${response.status})`)
      }

      const payload = await response.json()
      if (payload && payload.recording_url) {
        this._setLegacyLinks(payload.recording_url)
        if (this.hasErrorTarget) {
          this.errorTarget.textContent = "Replay available. Open in a new tab to watch."
          this.errorTarget.classList.remove("hidden")
        }
        return
      }

      const events = Array.isArray(payload) ? payload : payload.events
      if (!Array.isArray(events) || events.length === 0) {
        throw new Error("Recording empty")
      }

      this._renderPlayer(events)
      this._setDownload(events)
      if (this.hasOpenLinkTarget) {
        this.openLinkTarget.href = recordingUrl
      }
    } catch (error) {
      if (this.hasErrorTarget) {
        this.errorTarget.textContent = "Session replay unavailable. Try again later."
        this.errorTarget.classList.remove("hidden")
      }
    }
  }

  close(event) {
    event?.preventDefault?.()

    this._resetPlayer()
    this._closeModal()
  }

  backdropClick(event) {
    if (event.target === this.modalTarget) {
      this.close(event)
    }
  }

  _handleKeydown(event) {
    if (event.key !== "Escape") return
    if (!this.hasModalTarget) return
    if (this.modalTarget.classList.contains("hidden")) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.close()
  }

  _renderPlayer(events) {
    if (!this.hasPlayerTarget) return
    this.playerTarget.innerHTML = ""
    this.playerInstance = new rrwebPlayer({
      target: this.playerTarget,
      props: {
        events,
        autoPlay: false,
        showController: true,
        width: "100%",
        height: "100%",
      },
    })
  }

  _setDownload(events) {
    if (!this.hasDownloadLinkTarget) return

    if (this.downloadUrl) {
      URL.revokeObjectURL(this.downloadUrl)
      this.downloadUrl = null
    }

    const blob = new Blob([JSON.stringify(events)], { type: "application/json" })
    this.downloadUrl = URL.createObjectURL(blob)
    this.downloadLinkTarget.href = this.downloadUrl
    this.downloadLinkTarget.setAttribute("download", "session-replay.json")
  }

  _setLegacyLinks(url) {
    if (this.hasOpenLinkTarget) {
      this.openLinkTarget.href = url
    }
    if (this.hasDownloadLinkTarget) {
      this.downloadLinkTarget.href = url
      this.downloadLinkTarget.removeAttribute("download")
    }
  }

  _resetPlayer() {
    if (this.downloadUrl) {
      URL.revokeObjectURL(this.downloadUrl)
      this.downloadUrl = null
    }
    this.playerInstance = null
    if (this.hasPlayerTarget) {
      this.playerTarget.innerHTML = ""
    }
  }

  _openModal() {
    const controller = this.application.getControllerForElementAndIdentifier(this.modalTarget, "modal")
    if (controller) {
      controller.open()
      return
    }

    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  _closeModal() {
    const controller = this.application.getControllerForElementAndIdentifier(this.modalTarget, "modal")
    if (controller) {
      controller.close()
      return
    }

    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }
}
